import { useAuth0 } from '@auth0/auth0-react';
import * as Avatar from '@radix-ui/react-avatar';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Popover from '@radix-ui/react-popover';
import { Building2, Check, ChevronsUpDown, LogOut, Network, Settings, ShieldCheck, Users } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useOrgClaims } from '@/lib/hooks/useOrgClaims';
import { Tabs } from '@/lib/types/tabs';
import { buildOrgPath, stripOrgPrefix } from '@/lib/utils/orgRoutes';
import { TAB_TO_ROUTE, useTheme } from '@/providers';
import { isAdminOrg } from '../../constants';
import ThemeToggle from '../ThemeToggle/ThemeToggle';

export type NavigationBarProps = {
  logoSrc: string;
  logoAlt?: string;
  userAvatarSrc?: string;
  userName?: string;
  currentTab?: Tabs;
  defaultTab?: Tabs;
  onTabChange?: (tab: Tabs) => void;
  className?: string;
};

// Left side nav items
const LEFT_NAV_ITEMS: { key: Tabs; label: string }[] = [
  { key: Tabs.HOME, label: 'Home' },
  { key: Tabs.WORKSPACE, label: 'Workspace' },
  { key: Tabs.SCENARIOS, label: 'Scenarios' },
];

// Right side nav items
// Docs lives on the right, where Arks used to sit before the v0.6 cleanup
const RIGHT_NAV_ITEMS: { key: Tabs; label: string }[] = [
  { key: Tabs.DOCS, label: 'Docs' },
  { key: Tabs.ASSETS, label: 'Assets' },
  { key: Tabs.USAGE, label: 'Usage' },
];

// All nav items merged (for smaller screens)
const ALL_NAV_ITEMS: { key: Tabs; label: string }[] = [...LEFT_NAV_ITEMS, ...RIGHT_NAV_ITEMS];

function getInitials(name?: string): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Navigation list with sliding indicator
function NavList({
  items,
  activeTab,
  onSelect,
  className,
}: {
  items: { key: Tabs; label: string }[];
  activeTab: Tabs;
  onSelect: (tab: Tabs) => void;
  className?: string;
}) {
  const containerRef = React.useRef<HTMLUListElement>(null);
  const itemRefs = React.useRef<Map<Tabs, HTMLAnchorElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = React.useState({ left: 0, width: 0, opacity: 0 });

  // Measure and update indicator position
  const updateIndicatorPosition = React.useCallback(() => {
    const activeButton = itemRefs.current.get(activeTab);
    const container = containerRef.current;

    if (activeButton && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
        opacity: 1,
      });
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [activeTab]);

  // Update indicator position when active tab changes
  // Use useLayoutEffect to run synchronously after DOM mutations
  // Then use requestAnimationFrame to ensure layout has fully settled
  React.useLayoutEffect(() => {
    // Use double rAF to ensure we measure after the browser has completed layout
    // First rAF queues for next frame, second rAF runs after layout is complete
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(updateIndicatorPosition);
    });
    return () => cancelAnimationFrame(frameId);
  }, [updateIndicatorPosition]);

  // Also update on resize
  React.useEffect(() => {
    window.addEventListener('resize', updateIndicatorPosition);
    return () => window.removeEventListener('resize', updateIndicatorPosition);
  }, [updateIndicatorPosition]);

  const isActiveInThisList = items.some((item) => item.key === activeTab);

  return (
    <NavigationMenu.List
      ref={containerRef}
      className={['relative flex h-full shrink-0 items-center gap-2.5 sm:gap-3 md:gap-5', className]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Sliding indicator - positioned at absolute bottom of navbar */}
      <span
        className="absolute -bottom-[15px] h-[2px] rounded-full bg-gray-700 transition-all duration-200 ease-out dark:bg-white/80"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          opacity: isActiveInThisList ? indicatorStyle.opacity : 0,
        }}
      />

      {items.map((item) => {
        const isActive = activeTab === item.key;
        const href = TAB_TO_ROUTE[item.key];
        return (
          <NavigationMenu.Item key={item.key}>
            <NavigationMenu.Link asChild>
              <a
                ref={(el) => {
                  if (el) itemRefs.current.set(item.key, el);
                }}
                href={href}
                className={[
                  'relative flex h-full cursor-pointer items-center gap-1.5 px-1 text-[13.5px] leading-none outline-none transition-colors duration-150',
                  isActive
                    ? 'font-medium text-gray-700 dark:text-white/80'
                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                ].join(' ')}
                aria-current={isActive ? 'page' : undefined}
                onClick={(e) => {
                  // Allow cmd/ctrl + click to open in new tab
                  if (e.metaKey || e.ctrlKey) {
                    return;
                  }
                  e.preventDefault();
                  onSelect(item.key);
                }}
              >
                {item.label}
              </a>
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        );
      })}
    </NavigationMenu.List>
  );
}

export default function NavigationBar(props: NavigationBarProps) {
  const {
    logoSrc,
    logoAlt,
    userAvatarSrc,
    userName,
    currentTab,
    defaultTab = Tabs.WORKSPACE,
    onTabChange,
    className,
  } = props;

  const { logout: auth0logout, user } = useAuth0();
  const { orgId, orgName, organizations } = useOrgClaims();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  // Notebook is always enabled, so all nav items are shown
  const filteredLeftNavItems = LEFT_NAV_ITEMS;
  const filteredAllNavItems = ALL_NAV_ITEMS;

  // Determine if user can switch orgs (has more than one org)
  const canSwitchOrgs = organizations && organizations.length > 1;

  // Switch organization by navigating to the same page under the target org.
  // OrgUrlSync detects the org mismatch and performs the actual refresh-token
  // switch + reload, so link-opens and this picker share one code path.
  const handleSwitchOrg = (targetOrg: { id: string }) => {
    navigate(`${buildOrgPath(targetOrg.id, stripOrgPrefix(location.pathname))}${location.search}`);
  };

  const [internalTab, setInternalTab] = React.useState<Tabs>(defaultTab);
  const [orgPickerOpen, setOrgPickerOpen] = React.useState(false);
  const isControlled = typeof currentTab !== 'undefined';
  const activeTab = isControlled ? (currentTab as Tabs) : internalTab;

  const handleSelect = (tab: Tabs) => {
    if (!isControlled) {
      setInternalTab(tab);
    }
    if (onTabChange) onTabChange(tab);
  };

  const handleLogout = () => {
    auth0logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const initials = getInitials(userName || user?.name || user?.email);

  return (
    <NavigationMenu.Root
      className={[
        'fixed top-0 right-0 left-0 z-[1000] h-11 border-b font-sans transition-colors duration-200',
        'border-gray-200 bg-white',
        'dark:border-white/10 dark:bg-gray-950',
        className ?? '',
      ].join(' ')}
    >
      <div className="relative z-10 mx-auto flex h-full w-full min-w-0 items-center pr-3.5 pl-3 sm:pl-6">
        {/* Scrollable nav area */}
        <div className="scrollbar-none flex h-full min-w-0 flex-1 items-center overflow-x-auto overflow-y-hidden">
          {/* Logo + Left Nav (or All Nav on small screens) */}
          <div className="flex h-full shrink-0 items-center gap-2.5 sm:gap-3 md:gap-7">
            <button
              onClick={() => handleSelect(Tabs.HOME)}
              className="shrink-0 cursor-pointer outline-none"
              type="button"
              aria-label="Go to home"
            >
              <img
                src={logoSrc}
                alt={logoAlt ?? 'Logo'}
                className={`w-auto object-contain ${resolvedTheme === 'dark' ? 'h-6' : 'h-7'}`}
              />
            </button>
            {/* Merged nav items on smaller screens */}
            <div className="flex h-full items-center lg:hidden">
              <NavList items={filteredAllNavItems} activeTab={activeTab} onSelect={handleSelect} />
            </div>
            {/* Left nav items on larger screens */}
            <div className="hidden h-full items-center lg:flex">
              <NavList items={filteredLeftNavItems} activeTab={activeTab} onSelect={handleSelect} />
            </div>
          </div>

          {/* Spacer and Right Nav (only on large screens) */}
          <div className="hidden lg:flex lg:min-w-[16px] lg:flex-1" />
          <div className="hidden h-full items-center lg:flex">
            <NavList items={RIGHT_NAV_ITEMS} activeTab={activeTab} onSelect={handleSelect} />
          </div>

          {/* Spacer for smaller screens */}
          <div className="min-w-[8px] flex-1 sm:min-w-[16px] lg:hidden" />
        </div>

        {/* Actions (Theme, Avatar) - Always visible, outside scroll area */}
        <div className="flex h-full shrink-0 items-center gap-4 pl-4">
          {/* Theme Toggle - Always visible */}
          <div className="shrink-0">
            <ThemeToggle />
          </div>

          {/* User Avatar Dropdown - Always visible */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="shrink-0 cursor-pointer outline-none" aria-label="User menu">
                <Avatar.Root
                  className="inline-flex h-7 w-7 cursor-pointer select-none items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle ring-2 ring-transparent transition-all duration-150 hover:ring-gray-200 dark:bg-gray-800 dark:hover:ring-gray-700"
                  aria-label={userName || user?.name || 'User'}
                >
                  {userAvatarSrc || user?.picture ? (
                    <Avatar.Image
                      className="h-full w-full rounded-inherit object-cover"
                      src={userAvatarSrc || user?.picture}
                      alt={userName || user?.name || 'User'}
                    />
                  ) : null}
                  <Avatar.Fallback
                    className="flex h-full w-full items-center justify-center bg-gray-900 font-medium text-white text-xs dark:bg-gray-700"
                    delayMs={userAvatarSrc || user?.picture ? 600 : 0}
                  >
                    {initials}
                  </Avatar.Fallback>
                </Avatar.Root>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="relative top-2 right-1 w-[260px] rounded-xl border border-gray-100 bg-white p-2 shadow-gray-900/10 shadow-xl will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade dark:border-white/10 dark:bg-gray-900 dark:shadow-black/30"
                sideOffset={5}
              >
                <div className="rounded-lg bg-gray-50/80 p-2.5 ring-1 ring-gray-100 dark:bg-white/[0.04] dark:ring-white/5">
                  <div className="flex items-center gap-2.5">
                    <Avatar.Root
                      className="inline-flex size-9 shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-gray-900 align-middle dark:bg-gray-700"
                      aria-label={userName || user?.name || 'User'}
                    >
                      {userAvatarSrc || user?.picture ? (
                        <Avatar.Image
                          className="h-full w-full rounded-inherit object-cover"
                          src={userAvatarSrc || user?.picture}
                          alt={userName || user?.name || 'User'}
                        />
                      ) : null}
                      <Avatar.Fallback
                        className="flex h-full w-full items-center justify-center font-medium text-white text-xs"
                        delayMs={userAvatarSrc || user?.picture ? 600 : 0}
                      >
                        {initials}
                      </Avatar.Fallback>
                    </Avatar.Root>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900 text-sm dark:text-white">
                        {user?.name || userName || 'No name'}
                      </p>
                      <p className="truncate text-gray-500 text-xs dark:text-gray-400">{user?.email || 'No email'}</p>
                    </div>
                  </div>

                  <div className="mt-2 rounded-md bg-white px-2 py-1.5 ring-1 ring-gray-100 dark:bg-gray-950/40 dark:ring-white/10">
                    {/* Single org - just display */}
                    {!canSwitchOrgs && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="min-w-0">
                          <p className="font-medium text-[10px] text-gray-400 uppercase tracking-wide dark:text-gray-500">
                            Organization
                          </p>
                          <p className="truncate font-medium text-gray-900 text-xs dark:text-white">
                            {orgName || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Multiple orgs - searchable picker */}
                    {canSwitchOrgs && (
                      <Popover.Root open={orgPickerOpen} onOpenChange={setOrgPickerOpen}>
                        <Popover.Trigger asChild>
                          <button
                            type="button"
                            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left outline-none transition-[background-color,color] duration-150 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-white/5 dark:hover:text-white"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
                              <span className="min-w-0">
                                <span className="block font-medium text-[10px] text-gray-400 uppercase tracking-wide dark:text-gray-500">
                                  Organization
                                </span>
                                <span className="block truncate font-medium text-gray-900 text-xs dark:text-white">
                                  {orgName || 'Unknown'}
                                </span>
                              </span>
                            </span>
                            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          </button>
                        </Popover.Trigger>
                        <Popover.Portal>
                          <Popover.Content
                            className="z-[1100] w-[220px] rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                            sideOffset={4}
                            align="start"
                          >
                            <Command>
                              <CommandInput placeholder="Search organizations..." />
                              <CommandList>
                                <CommandEmpty>No organizations found.</CommandEmpty>
                                <CommandGroup>
                                  {organizations?.map((org) => (
                                    <CommandItem
                                      key={org.id}
                                      value={org.display_name}
                                      onSelect={() => {
                                        if (org.id !== orgId) {
                                          handleSwitchOrg(org);
                                        }
                                        setOrgPickerOpen(false);
                                      }}
                                      className="flex items-center gap-2"
                                    >
                                      <Building2 className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                      <span className="flex-1">{org.display_name}</span>
                                      {org.id === orgId && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </Popover.Content>
                        </Popover.Portal>
                      </Popover.Root>
                    )}
                  </div>
                </div>

                {/* Team */}
                <DropdownMenu.Item
                  className="group mt-1.5 flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-[13px] text-gray-600 leading-none outline-none transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 dark:text-gray-300 dark:data-[disabled]:text-gray-600 dark:hover:bg-white/5 dark:hover:text-white"
                  onSelect={() => onTabChange?.(Tabs.TEAM)}
                >
                  <Users className="h-3.5 w-3.5" />
                  Team
                </DropdownMenu.Item>

                {/* Settings */}
                <DropdownMenu.Item
                  className="group flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-[13px] text-gray-600 leading-none outline-none transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 dark:text-gray-300 dark:data-[disabled]:text-gray-600 dark:hover:bg-white/5 dark:hover:text-white"
                  onSelect={() => onTabChange?.(Tabs.SETTINGS)}
                >
                  <Settings className="h-3.5 w-3.5" />
                  Settings
                </DropdownMenu.Item>

                {/* Admin - only show for admin org users */}
                {isAdminOrg(orgId) && (
                  <DropdownMenu.Item
                    className="group flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-[13px] text-gray-600 leading-none outline-none transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 dark:text-gray-300 dark:data-[disabled]:text-gray-600 dark:hover:bg-white/5 dark:hover:text-white"
                    onSelect={() => onTabChange?.(Tabs.ADMIN)}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Admin
                  </DropdownMenu.Item>
                )}

                {/* Antioch Ontology - only show for admin org users */}
                {isAdminOrg(orgId) && (
                  <DropdownMenu.Item
                    className="group flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-[13px] text-gray-600 leading-none outline-none transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 dark:text-gray-300 dark:data-[disabled]:text-gray-600 dark:hover:bg-white/5 dark:hover:text-white"
                    onSelect={() => onTabChange?.(Tabs.ONTOLOGY)}
                  >
                    <Network className="h-3.5 w-3.5" />
                    Ontology
                  </DropdownMenu.Item>
                )}

                <DropdownMenu.Separator className="mx-1 my-1 h-px bg-gray-100 dark:bg-white/10" />

                <DropdownMenu.Item
                  className="group flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-[13px] text-gray-600 leading-none outline-none transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 dark:text-gray-300 dark:data-[disabled]:text-gray-600 dark:hover:bg-white/5 dark:hover:text-white"
                  onSelect={handleLogout}
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Logout
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </NavigationMenu.Root>
  );
}
